import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowLeft, FloppyDisk, FilePdf, PaperPlaneTilt } from "@phosphor-icons/react";
import client from "@/lib/api";
import SignaturePad from "@/components/SignaturePad";
import { Section, In, Sel } from "./Fields";
import { STATUSES, SIGNATURE_STATUS_PILL } from "./constants";

export default function ReportStep({ inspection, patch, onPrev, downloadPDF }) {
  const [summary, setSummary] = useState(inspection.summary || "");
  const [mr, setMr] = useState(inspection.meter_readings || { electricity: "", gas: "", water: "" });
  const [status, setStatus] = useState(inspection.status);
  const [inspectorSig, setInspectorSig] = useState(inspection.inspector_signature || "");

  // Signature flow
  const [tenantEmail, setTenantEmail] = useState(
    inspection.tenant_email || inspection.tenant?.email || ""
  );
  const [landlordEmail, setLandlordEmail] = useState(
    inspection.landlord_email || inspection.landlord?.email || ""
  );
  const [sigStatus, setSigStatus] = useState(inspection.signature_status || {
    tenant: { status: "Pending" }, landlord: { status: "Pending" },
  });
  const [sending, setSending] = useState({ tenant: false, landlord: false });
  const [docusignConfigured, setDocusignConfigured] = useState(false);

  // Email-pdf flow
  const defaultRecipients = [inspection.landlord?.email, inspection.tenant?.email]
    .filter(Boolean).join(", ");
  const [recipients, setRecipients] = useState(defaultRecipients);
  const [emailing, setEmailing] = useState(false);

  useEffect(() => {
    client.get(`/inspections/${inspection.id}/signature/status`).then((r) => {
      setSigStatus(r.data.signature_status);
      setDocusignConfigured(r.data.docusign_configured);
      if (r.data.tenant_email) setTenantEmail(r.data.tenant_email);
      if (r.data.landlord_email) setLandlordEmail(r.data.landlord_email);
    }).catch(() => {});
  }, [inspection.id]);

  const save = async () => {
    await patch({
      summary, meter_readings: mr, status,
      inspector_signature: inspectorSig,
      tenant_email: tenantEmail, landlord_email: landlordEmail,
    });
    toast.success("Report saved");
  };

  const sendForSignature = async (party) => {
    const email = party === "tenant" ? tenantEmail : landlordEmail;
    const name = party === "tenant" ? inspection.tenant?.name : inspection.landlord?.name;
    if (!email) {
      toast.error(`Add the ${party} email first`);
      return;
    }
    setSending({ ...sending, [party]: true });
    try {
      const { data } = await client.post(
        `/inspections/${inspection.id}/signature/send`,
        { party, email, name: name || "" }
      );
      setSigStatus(data.signature_status);
      toast.success(
        data.mocked
          ? `${party[0].toUpperCase() + party.slice(1)} signature request queued (DocuSign keys not configured — mock envelope ${data.signature_status[party].envelope_id})`
          : `${party[0].toUpperCase() + party.slice(1)} signature request sent via DocuSign`
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally {
      setSending({ ...sending, [party]: false });
    }
  };

  const overrideStatus = async (party, newStatus) => {
    try {
      const { data } = await client.post(
        `/inspections/${inspection.id}/signature/status`,
        { party, status: newStatus }
      );
      setSigStatus(data.signature_status);
      toast.success("Status updated");
    } catch {
      toast.error("Failed to update status");
    }
  };

  const sendEmail = async () => {
    const list = recipients.split(/[,;\s]+/).map((s) => s.trim()).filter(Boolean);
    if (!list.length) { toast.error("Add at least one recipient"); return; }
    setEmailing(true);
    try {
      await client.post(`/inspections/${inspection.id}/email`, { recipients: list });
      toast.success(`Report emailed to ${list.length} recipient(s)`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Email failed (is RESEND_API_KEY set?)");
    } finally {
      setEmailing(false);
    }
  };

  return (
    <Section title="Report" subtitle="Meter readings, signatures, and PDF export.">
      <div className="grid md:grid-cols-3 gap-3 mb-6">
        <In label="Electricity reading" v={mr.electricity} on={(v) => setMr({ ...mr, electricity: v })} />
        <In label="Gas reading" v={mr.gas} on={(v) => setMr({ ...mr, gas: v })} />
        <In label="Water reading" v={mr.water} on={(v) => setMr({ ...mr, water: v })} />
      </div>

      <label className="block mb-4">
        <span className="text-xs font-semibold uppercase tracking-wider text-[#6B7280]">Final summary</span>
        <textarea
          value={summary}
          onChange={(e) => setSummary(e.target.value)}
          rows={5}
          className="trent-input mt-1"
          data-testid="report-summary"
          placeholder="Overall condition, recommendations, deductions, follow-ups…"
        />
      </label>

      <Sel label="Inspection status" v={status} on={setStatus} opts={STATUSES} test="report-status" />

      <div className="mt-8">
        <div className="text-[10px] uppercase tracking-[0.25em] text-[#6B7280] mb-3 font-semibold">
          Inspector signature
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <SignaturePad label="Inspector" value={inspectorSig} onChange={setInspectorSig} testId="sig-inspector" />
        </div>
      </div>

      <SignatureBlock
        party="tenant"
        email={tenantEmail}
        onEmail={setTenantEmail}
        entry={sigStatus.tenant}
        sending={sending.tenant}
        onSend={() => sendForSignature("tenant")}
        onOverride={(s) => overrideStatus("tenant", s)}
        docusignConfigured={docusignConfigured}
      />
      <SignatureBlock
        party="landlord"
        email={landlordEmail}
        onEmail={setLandlordEmail}
        entry={sigStatus.landlord}
        sending={sending.landlord}
        onSend={() => sendForSignature("landlord")}
        onOverride={(s) => overrideStatus("landlord", s)}
        docusignConfigured={docusignConfigured}
      />

      <div className="mt-6 border border-[#E5E7EB] rounded-md p-4 bg-[#FAFAF7]">
        <div className="text-[10px] uppercase tracking-[0.25em] text-[#6B7280] mb-2 font-semibold">
          Email report
        </div>
        <div className="flex flex-col md:flex-row gap-2">
          <input
            value={recipients}
            onChange={(e) => setRecipients(e.target.value)}
            placeholder="recipient1@example.ie, recipient2@example.ie"
            className="trent-input flex-1"
            data-testid="email-recipients"
          />
          <button onClick={sendEmail} disabled={emailing} className="trent-btn-secondary" data-testid="email-send-btn">
            <PaperPlaneTilt size={14} /> {emailing ? "Sending…" : "Email PDF"}
          </button>
        </div>
      </div>

      <div className="flex flex-wrap gap-3 mt-8 pt-6 border-t border-[#E5E7EB]">
        <button onClick={onPrev} className="trent-btn-secondary">
          <ArrowLeft weight="bold" size={14} /> Back
        </button>
        <button onClick={save} className="trent-btn-secondary" data-testid="report-save-btn">
          <FloppyDisk size={14} /> Save
        </button>
        <button onClick={downloadPDF} className="trent-btn-accent ml-auto" data-testid="report-download-btn">
          <FilePdf size={16} weight="duotone" /> Download PDF report
        </button>
      </div>
    </Section>
  );
}

function SignatureBlock({ party, email, onEmail, entry, sending, onSend, onOverride, docusignConfigured }) {
  const label = party === "tenant" ? "Tenant" : "Landlord";
  const status = entry?.status || "Pending";
  return (
    <div className="mt-6 border border-[#E5E7EB] rounded-md p-5 bg-white" data-testid={`signature-block-${party}`}>
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-[#6B7280] font-semibold">
            {label} digital signature
          </div>
          <div className="trent-heading font-bold text-[#1E3A5F] text-lg">
            Send {label.toLowerCase()} for signature
          </div>
        </div>
        <span className={`condition-pill ${SIGNATURE_STATUS_PILL[status] || ""}`} data-testid={`sig-status-${party}`}>
          {status}
        </span>
      </div>
      <div className="grid md:grid-cols-[1fr_auto_auto] gap-2 items-end">
        <label className="block">
          <span className="text-[10px] uppercase tracking-wider text-[#6B7280] font-semibold">
            {label} email
          </span>
          <input
            type="email"
            value={email}
            onChange={(e) => onEmail(e.target.value)}
            placeholder={`${party}@example.ie`}
            className="trent-input mt-1"
            data-testid={`${party}-email-input`}
          />
        </label>
        <button onClick={onSend} disabled={sending} className="trent-btn-primary" data-testid={`send-${party}-signature`}>
          <PaperPlaneTilt size={14} /> {sending ? "Sending…" : `Send to ${label}`}
        </button>
        <select
          value={status}
          onChange={(e) => onOverride(e.target.value)}
          className="trent-input"
          data-testid={`override-${party}-status`}
          title="Admin override (until DocuSign webhook is wired)"
        >
          {["Pending", "Sent", "Signed", "Declined"].map((s) => <option key={s}>{s}</option>)}
        </select>
      </div>
      {entry?.envelope_id && (
        <div className="text-[10px] text-[#94A3B8] mt-2 font-mono">
          envelope: {entry.envelope_id}
          {entry.mocked && (
            <span className="ml-2 text-[#92400E] bg-[#FEF3C7] px-1.5 py-0.5 rounded">
              MOCK · DocuSign credentials not configured
            </span>
          )}
        </div>
      )}
      {!docusignConfigured && (
        <div className="text-[10px] text-[#94A3B8] mt-2">
          Set <code>DOCUSIGN_INTEGRATION_KEY</code>, <code>DOCUSIGN_USER_ID</code>, <code>DOCUSIGN_ACCOUNT_ID</code> and <code>DOCUSIGN_PRIVATE_KEY</code> in backend .env to enable real DocuSign envelopes.
        </div>
      )}
    </div>
  );
}
